import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: string;
  children: React.ReactElement;
  delay?: number;
  contentStyle?: React.CSSProperties;
}

export default function Tooltip({ content, children, delay = 0, contentStyle }: TooltipProps) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const childRef = useRef<HTMLElement | null>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // 이벤트 정보를 미리 저장 (React 이벤트 풀링 이슈 방지)
    const target = e.currentTarget as HTMLElement;
    if (!target) return;
    
    const rect = target.getBoundingClientRect();
    
    // 임시 요소를 만들어서 툴팁 너비 측정
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.padding = '7px 15px';
    tempDiv.style.fontSize = '14px';
    tempDiv.textContent = content;
    document.body.appendChild(tempDiv);
    const tooltipWidth = tempDiv.offsetWidth;
    document.body.removeChild(tempDiv);
    
    // 기본 위치 계산
    let left = rect.left + rect.width / 2 + window.scrollX;
    const top = rect.bottom + window.scrollY + 5;
    
    // 화면 오른쪽 경계 체크
    const tooltipRight = left + tooltipWidth / 2;
    if (tooltipRight > window.innerWidth) {
      left = window.innerWidth - tooltipWidth / 2 - 10; // 10px 마진
    }
    
    // 화면 왼쪽 경계 체크
    const tooltipLeft = left - tooltipWidth / 2;
    if (tooltipLeft < 0) {
      left = tooltipWidth / 2 + 10; // 10px 마진
    }
    
    const newPosition = { top, left };
    
    timeoutRef.current = setTimeout(() => {
      setPosition(newPosition);
      setShow(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShow(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const tooltipElement = show
    ? createPortal(
        <div
          style={{
            position: "absolute",
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translateX(-50%)",
            background: "#222",
            color: "#fff",
            padding: "7px 15px",
            borderRadius: "9px",
            fontSize: "14px",
            whiteSpace: "nowrap",
            zIndex: 99999,
            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
            pointerEvents: "none",
            ...contentStyle,
          }}
        >
          {content}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {React.cloneElement(children, {
        onMouseEnter: (e: React.MouseEvent) => {
          handleMouseEnter(e);
          const originalHandler = (children.props as any).onMouseEnter;
          if (typeof originalHandler === 'function') {
            originalHandler(e);
          }
        },
        onMouseLeave: (e: React.MouseEvent) => {
          handleMouseLeave();
          const originalHandler = (children.props as any).onMouseLeave;
          if (typeof originalHandler === 'function') {
            originalHandler(e);
          }
        },
      } as any)}
      {tooltipElement}
    </>
  );
}
